import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../Services/Store";
import { fetchUserInfo, fetchUserAccess } from "../../Services/Slices/authSlice";
import { useAuth } from "../../Contexts/AuthContext";
import WebService from "../../Services/WebService";
import { PORTAL_AUTH_CLIENT } from "../../Services/authClient";
import Swal from "sweetalert2";
import "./log.css";
import { ToastContainer, toast } from "react-toastify";
import {
  FaGooglePlay,
  FaDownload,
  FaApple,
  FaCheckCircle,
  FaPhone,
  FaEnvelope,
} from "react-icons/fa";

type AppVersionResp = {
  latest_version_code: number;
  min_supported_version: number;
  latest_update_url: string;
  latest_update_version_name: string;
};

const Login = () => {
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ mobile?: string; password?: string }>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [appMeta, setAppMeta] = useState<AppVersionResp | null>(null);
  const [appMetaLoading, setAppMetaLoading] = useState<boolean>(true);

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { setAuthenticated } = useAuth();
  const LOGIN_ERROR_TOAST_ID = "login-error";

  useEffect(() => {
    return () => {
      toast.clearWaitingQueue?.();
      toast.dismiss();
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res: any = await WebService.getAPI?.({
          action: "/v1/door-management/app/version",
          isShowError: false,
        });
        const payload: AppVersionResp = res?.data ?? res;

        if (
          mounted &&
          payload &&
          typeof payload.latest_version_code !== "undefined" &&
          payload.latest_update_url
        ) {
          setAppMeta(payload);
        }
      } catch (e) {
        console.error("Failed to load app version:", e);
      } finally {
        if (mounted) setAppMetaLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const downloadUrl = "https://kunaldoorandmore.com/kunaldoorandmore-app-v2.apk";

  const downloadName = appMeta
    ? `${appMeta.latest_update_version_name}-V${appMeta.latest_version_code}`
    : "Kunaldoorandmore-app-v2";

  const validateFields = () => {
    let valid = true;
    const err: { mobile?: string; password?: string } = {};

    if (!mobile) {
      err.mobile = "Mobile number is required";
      valid = false;
    } else if (!/^\d{10}$/.test(mobile)) {
      err.mobile = "Enter a valid 10-digit mobile number";
      valid = false;
    }

    if (!password) {
      err.password = "Password is required";
      valid = false;
    } else if (password.length < 6) {
      err.password = "Password must be at least 6 characters";
      valid = false;
    }

    setErrors(err);
    return valid;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);

    if (!validateFields()) return;

    setLoading(true);
    try {
      const data: any = await WebService.postAPI({
        action: "/v1/door-management/login",
        body: { mobile_number: mobile, password: password, client: PORTAL_AUTH_CLIENT },
        isShowError: false,
      });

      if (data?.status_code === 200) {
        toast.clearWaitingQueue?.();
        toast.dismiss();
        // OTP disabled: backend returns JWT immediately
        if (data?.token) {
          localStorage.setItem("token", data.token);
          setAuthenticated(true);
          const userRes: any = await dispatch(fetchUserInfo());
          await dispatch(fetchUserAccess());
          const userType = (
            (fetchUserInfo.fulfilled.match(userRes) && userRes.payload?.user_type) ||
            data?.data?.user_type ||
            ""
          )
            .toString()
            .toUpperCase();

          await Swal.fire({
            icon: "success",
            title: "Login Successful",
            showConfirmButton: false,
            timer: 900,
          });

          if (userType === "CUSTOMER") {
            navigate("/shop", { replace: true });
          } else {
            navigate("/", { replace: true });
          }
          return;
        }

        Swal.fire({
          icon: "success",
          title: "OTP Sent",
          text: "Please verify OTP to complete login.",
          confirmButtonColor: "#FFD400",
        }).then(() => {
          navigate("/verifyOtp", { state: { mobile, type: "LOGIN" } });
        });
      } else if (data?.message && String(data.message).toLowerCase().includes("user not found")) {
        const msg = "User not found. Please sign up to continue.";
        setServerError(msg);
        toast.error(msg, { containerId: "auth", toastId: LOGIN_ERROR_TOAST_ID });
      } else {
        const msg = data?.message || "Invalid mobile number or password.";
        setServerError(msg);
        toast.error(msg, { containerId: "auth", toastId: LOGIN_ERROR_TOAST_ID });
      }
    } catch (err: any) {
      // WebService.errorHandler rejects with a string message
      const status = err?.response?.status ?? err?.status;
      const msg =
        (typeof err === "string" && err.trim() ? err : null) ||
        err?.response?.data?.message ||
        err?.data?.message ||
        (status === 401
          ? "Invalid mobile number or password."
          : status === 404
          ? "User not found. Please sign up to continue."
          : null) ||
        (typeof err?.message === "string" &&
        !/network error/i.test(err.message)
          ? err.message
          : null) ||
        "Invalid mobile number or password.";

      setServerError(msg);
      toast.error(msg, { containerId: "auth", toastId: LOGIN_ERROR_TOAST_ID });
    } finally {
      setLoading(false);
    }
  };

  const hasServerError = !!serverError;

  return (
    <div className="page-container">
      {/* LEFT — scrollable */}
      <aside className="about-pane">
        <div className="about-inner">
          <header className="about-header">
            <img src="/Assets/dm.png" alt="Kunal Door and More" className="about-logo" />
            <div>
              <h1 className="about-title">Kunal Door and More</h1>
              <p className="about-kicker">Custom doors • Smart tracking • Reliable updates</p>
            </div>
          </header>

          <section className="about-card">
            <h2 className="section-title">About Us</h2>
            <p className="section-text">
              Kunal Door and More is your one-stop portal for customized door solutions. Manage
              requests, track order status, and get reliable updates — all in one place.
            </p>
          </section>

          <section className="about-card">
            <h2 className="section-title">Services We Provide</h2>
            <ul className="bullet-list">
              <li><FaCheckCircle /> Customize Doors</li>
              <li><FaCheckCircle /> Track Your Order Status</li>
            </ul>
            <div className="pill-row">
              <span className="pill">Fast quotes</span>
              <span className="pill">Secure portal</span>
              <span className="pill">Real-time updates</span>
            </div>
          </section>

          <section className="about-card">
            <h2 className="section-title">We Are Available At</h2>
            <p className="section-text">🌐 Web Portal &nbsp; | &nbsp; 📱 Android &nbsp; | &nbsp; 🍏 iOS</p>x
          </section>

          <section className="about-card">
            <h2 className="section-title">Contact Us</h2>
            <p className="contact-line"><FaPhone />+91 98930 66415</p>
            <p className="contact-line"><FaEnvelope />Kunaldoors@gmail.com</p>
          </section>

          <div style={{ height: 24 }} />
        </div>
      </aside>

      {/* RIGHT — fixed, non-scroll */}
      <main className="login-pane">
        <ToastContainer
          containerId="auth"
          position="top-center"
          autoClose={2500}
          newestOnTop
          limit={2}
          pauseOnFocusLoss={false}
          closeOnClick
          draggable
        />

        <form className="login-form" onSubmit={handleLogin} noValidate>
          <img src="/Assets/dm.png" alt="Logo" className="login-logo" />
          <h2 className="login-heading">
            Welcome to Kunal Doorandmore! <span role="img" aria-label="wave">👋</span>
          </h2>
          <p className="login-subheading">Enter your mobile number and password.</p>

          <input
            type="tel"
            className={`login-input${(errors.mobile || hasServerError) ? " login-input-error" : ""}`}
            placeholder="Enter mobile number"
            value={mobile}
            maxLength={10}
            autoComplete="tel"
            onChange={e => {
              setMobile(e.target.value.replace(/\D/g, ""));
              if (errors.mobile) setErrors({ ...errors, mobile: undefined });
              if (serverError) setServerError(null);
            }}
            aria-invalid={!!errors.mobile || hasServerError}
            style={{ marginBottom: 6 }}
          />
          {errors.mobile && <div className="login-error-msg">{errors.mobile}</div>}

          <div className="login-password-group">
            <input
              type={showPassword ? "text" : "password"}
              className={`login-input${(errors.password || hasServerError) ? " login-input-error" : ""}`}
              placeholder="Password"
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                if (errors.password) setErrors({ ...errors, password: undefined });
                if (serverError) setServerError(null);
              }}
              minLength={6}
              autoComplete="current-password"
              aria-invalid={!!errors.password || hasServerError}
              style={{ paddingRight: 44, marginBottom: 6 }}
            />
            <button
              type="button"
              className="password-eye-btn"
              onClick={() => setShowPassword(prev => !prev)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" stroke="#888" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M2 12s4.5-7 10-7 10 7 10 7-4.5 7-10 7-10-7-10-7z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" stroke="#888" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M17.94 17.94A10.08 10.08 0 0112 19c-5.5 0-10-7-10-7a16.52 16.52 0 013.25-3.94m3.08-2.36A10.05 10.05 0 0112 5c5.5 0 10 7 10 7a16.4 16.4 0 01-4.22 5.31M1 1l22 22" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>

          {errors.password && <div className="login-error-msg">{errors.password}</div>}
          {serverError && <div className="login-error-msg" role="alert">{serverError}</div>}

          <div className="login-forgot">
            <Link to="/forgotpassword" className="login-forgot-link">Forgot password?</Link>
          </div>

          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? "Logging in..." : "Login"}
          </button>

          <p className="login-footer">
            New user? <Link to="/signuprequest" className="login-register-link">Sign up here!</Link>
          </p>

          <div className="store-row">
            <div className="store-row-title">Platforms</div>

            <a
              href={downloadUrl}
              className={`store-btn play`}
              download={downloadName}
              aria-label={`Download ${downloadName}`}
              title={`Download ${downloadName}`}
              onClick={(e) => {
                toast.info(`Downloading ${downloadName}`, { containerId: "auth" });
              }}
            >
              <FaGooglePlay aria-hidden="true" />
              <span className="store-btn-text">
                <strong>{appMetaLoading ? "Loading..." : "Download APK"}</strong>
              </span>
              <FaDownload className="trailing-icon" aria-hidden="true" />
            </a>

            <button
              type="button"
              className="store-btn appstore disabled"
              aria-label="App Store (coming soon)"
              title="Coming soon"
              disabled
            >
              <FaApple aria-hidden="true" />
              <span className="store-btn-text">
                <small>COMING SOON</small>
                <strong>App Store</strong>
              </span>
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default Login;
